import React, {FC, useEffect, useState} from 'react';
import {NextState} from '../../../types';
import {APIActionStatus, Form, formsStore, Record} from '../../../../../stores/Form';
import {useFooterContext} from '../../../hooks';
import styles from './CloseCashiersStep.module.scss';
import {CheckBox, Flex, TableColumn, Text} from '@priority-software/priority-style-react';
import texts from '../../../texts';
import {useObserver} from 'mobx-react';
import {getCashForm} from '../../../actions/compareCashierBalancesStep';
import {Loader, Table} from '../../../Components';
import {useFiscalYear} from '../../../hooks/useFiscalYear';
import {priority} from '@priority-software/priority-proto-api/lib/nform';
import classnames from 'classnames';

const cashColumnNames = ['CASHNAME', 'CASHDES', 'BALANCE3', 'CLOSEDATE'];
const closeDateColumn = 'CLOSEDATE';

export const CloseCashiersStep: FC = () => {
    const [cashFormLoading, setCashFormLoading] = useState(false);
    const [cashFormUpdating, setCashFormUpdating] = useState(false);
    const footerState = useFooterContext();
    const [fiscalYearStateLoaded, fiscalYearState] = useFiscalYear();
    const [isCloseState, setIsCloseState] = useState<boolean[]>([]);
    const [cashForm, setCashForm] = useState<Form | null>(null);

    useEffect(() => {
        footerState.setIsValid(!!cashForm);
    }, [cashForm]);

    const cashRecords = useObserver(() => cashForm?.records.filter(x => !x.isNew));

    useEffect(() => {
        footerState.setCustomNextLabel(
            texts['stage1_step3_custom_next'].replace('{#}', isCloseState.filter(x => !!x).length)
        );
    }, [isCloseState]);

    useEffect(() => {
        if (footerState.onNextState === NextState.Processing) {
            const handleNextButtonClick = async () => {
                setCashFormUpdating(true);
                footerState.setIsValid(false);

                const closeDateColumnId = cashForm.getColumnByName(closeDateColumn).id;

                for (const index in cashRecords) {
                    if (isCloseState[index]) {
                        const value = fiscalYearState?.prevEndDate;
                        cashRecords[index].getField(closeDateColumnId).setCurrentValue(value);
                        cashForm.setCurrentRow(cashRecords[index].recordIndex);
                        cashForm.setCurrentColumnId(closeDateColumnId);
                        const status = await formsStore
                            .getScreenBySession(cashForm.session)
                            .fieldUpdate(cashForm.activeField());
                        if (status === APIActionStatus.passed) {
                            await formsStore.getScreenBySession(cashForm.session).saveRecord();
                        }
                    }
                }
                setCashFormUpdating(false);
                formsStore.deleteForm(cashForm.session);
                footerState.setCustomNextLabel(undefined);
                footerState.setOnNextState(NextState.Done);
                footerState.setIsValid(true);
            };

            handleNextButtonClick();
        }
    }, [footerState.onNextState]);

    useEffect(() => {
        if (fiscalYearStateLoaded) {
            setCashFormLoading(true);
            getCashForm().then(form => {
                setCashForm(form);
                setCashFormLoading(false);
                setIsCloseState(form.records.filter(x => !x.isNew).map(() => true));
            });
        }
    }, [fiscalYearStateLoaded]);

    const tableColumns: TableColumn[] = useObserver(() =>
        cashForm
            ? [
                  ...cashForm.columns
                      .filter(column => cashColumnNames.includes(column.Name))
                      .map(
                          column =>
                              ({
                                  key: column.Title,
                                  title: column.Title,
                                  dataIndex: column.Title,
                                  render: (_, row: Record) => {
                                      const forceLtr =
                                          column.Type !== priority.netitems.form.ColumnType.Str &&
                                          column.Type !== priority.netitems.form.ColumnType.Rstr;
                                      return (
                                          <Text
                                              tag={'span'}
                                              skin={'paragraph-2'}
                                              componentClasses={classnames({
                                                  ['forceLtr']: forceLtr
                                              })}
                                          >
                                              {row.getField(column.id).currentValue}
                                          </Text>
                                      );
                                  }
                              } as TableColumn)
                      ),
                  {
                      key: texts['stage_1_step3_close_column'],
                      title: texts['stage_1_step3_close_column'],
                      dataIndex: texts['stage_1_step3_close_column'],
                      render: (_, row: Record) => {
                          const onChangeHandler = (index: number) => {
                              const newArr = [...isCloseState];
                              newArr[index] = !newArr[index];
                              setIsCloseState(newArr);
                          };

                          return (
                              <CheckBox
                                  checked={isCloseState[row.recordIndex]}
                                  onChange={() => onChangeHandler(row.recordIndex)}
                              />
                          );
                      }
                  } as TableColumn
              ]
            : []
    );

    const tableVisible = !cashFormLoading && !!cashRecords && fiscalYearStateLoaded;

    return (
        <Flex componentClasses={styles.wrapper} direction={'column'}>
            <Text tag={'span'} skin={'paragraph-1'} textColor={'primary-700'}>
                {texts['stage1_step3_explanation']}
            </Text>
            {tableVisible && (
                <Table
                    rowKey={obj => obj.recordIndex}
                    componentClasses={styles.table}
                    columns={tableColumns}
                    data={cashRecords}
                />
            )}
            <Loader
                isVisible={cashFormLoading || cashFormUpdating}
                componentClassname={styles.loader}
            />
        </Flex>
    );
};
